import React, { useEffect, useState, useRef } from 'react';
import StatusCard from './StatusCard';
import { DailyScrape, ScraperData } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import { XIcon } from 'lucide-react';
interface ScraperStatusMapProps {
  scraperData: {
    scrapers: {
      id: number;
      name: string;
    }[];
    dailyData: DailyScrape[];
  };
  viewMode: 'day' | 'week' | 'month';
  colorFilter: string | null;
}
const ScraperStatusMap: React.FC<ScraperStatusMapProps> = ({
  scraperData,
  viewMode,
  colorFilter
}) => {
  const {
    theme
  } = useTheme();
  const {
    scrapers,
    dailyData
  } = scraperData;
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleDays, setVisibleDays] = useState<DailyScrape[]>(dailyData);
  const [columnWidth, setColumnWidth] = useState<string>('auto');
  const [selectedCard, setSelectedCard] = useState<SelectedCardData | null>(null);
  // Calculate how many days to show based on container width
  useEffect(() => {
    const updateVisibleDays = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      // Fixed width for the left columns (ID + Name)
      const leftColumnWidth = 200; // Consistent width for left columns
      // Estimated width for each day column (2 cards per day)
      const dayColumnWidth = 60; // Consistent column width
      // Calculate how many days can fit
      const availableWidth = containerWidth - leftColumnWidth;
      const daysToShow = Math.max(1, Math.floor(availableWidth / dayColumnWidth));
      // Show most recent days first
      const visibleData = dailyData.slice(-daysToShow);
      setVisibleDays(visibleData);
      // Calculate column width to fill container completely
      if (visibleData.length > 0) {
        const calculatedWidth = `${availableWidth / visibleData.length}px`;
        setColumnWidth(calculatedWidth);
      }
    };
    // Initial calculation
    updateVisibleDays();
    // Recalculate on window resize
    const handleResize = () => updateVisibleDays();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dailyData, viewMode]);
  // Handle card click to show modal
  const handleCardClick = (data: ScraperData, time: 'AM' | 'PM') => {
    setSelectedCard({
      data,
      time
    });
  };
  // Close modal
  const closeModal = () => {
    setSelectedCard(null);
  };
  // Use consistent row height for all view modes
  const getRowHeight = () => {
    return 'h-10'; // Consistent compact height for all views
  };
  // Use consistent header height
  const getHeaderHeight = () => {
    return 'h-12'; // Consistent header height
  };
  // Format date to show MM/DD format and day of week
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If parsing fails, try to extract just the day from the string
      return dateString;
    }
    // Get day of week
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayOfWeek = days[date.getDay()];
    // Format as "MM/DD"
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return {
      date: `${month}/${day}`,
      day: dayOfWeek
    };
  };
  // Extract ID and Name from combined string
  const extractNameAndId = (scraper: {
    id: number;
    name: string;
  }) => {
    const nameParts = scraper.name.split(' ');
    const id = nameParts[0];
    const name = nameParts.slice(1).join(' ');
    return {
      id,
      name
    };
  };
  // Get the background color for the status card (copied from StatusCard for modal)
  const getCardBackgroundColor = (data: ScraperData) => {
    const status = getStatusFromDelta(data);
    switch (status) {
      case 'broken':
        return '#ff00cc';
      // Pink for broken
      case 'needsAttention':
        return '#ef4444';
      // Red for needs attention
      case 'major':
        return '#f97316';
      // Orange for major
      case 'moderate':
        return '#f59e0b';
      // Amber for moderate
      case 'minor':
        return '#eab308';
      // Yellow for minor
      case 'routine':
        return '#84cc16';
      // Lime green for routine
      case 'minimal':
        return '#22c55e';
      // Green for minimal
      case 'stable':
        return '#4ade80';
      // Light green for stable
      default:
        return '#7f8aa1';
      // Grey for unknown status
    }
  };
  // Determine status based on URL change thresholds (copied from StatusCard for modal)
  const getStatusFromDelta = (data: ScraperData) => {
    const delta = Math.abs(data.delta);
    const percentChange = data.previousUrls ? delta / data.previousUrls * 100 : 0;
    // For broken status (>90% drop)
    if (data.delta < 0 && percentChange >= 90) {
      return 'broken';
    }
    // For other statuses based on absolute change
    if (delta >= 50) {
      return 'needsAttention';
    }
    if (delta >= 40 && delta <= 49) {
      return 'major';
    }
    if (delta >= 30 && delta <= 39) {
      return 'moderate';
    }
    if (delta >= 20 && delta <= 29) {
      return 'minor';
    }
    if (delta >= 10 && delta <= 19) {
      return 'routine';
    }
    if (delta >= 1 && delta <= 9) {
      return 'minimal';
    }
    return 'stable';
  };
  // Check if a card should be visible based on the color filter
  const isCardVisible = (data?: ScraperData) => {
    if (!data) return colorFilter === '#7f8aa1'; // Only show empty cells if grey filter is selected
    if (!colorFilter) return true; // Show all if no filter is active
    // Get the background color for this card
    const cardColor = getCardBackgroundColor(data);
    // Check if it matches the filter
    return cardColor === colorFilter;
  };
  return <div ref={containerRef} className={`rounded-lg border w-full ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border-gray-200'}`} data-id="element-199">
      <div className="grid grid-flow-col auto-cols-max" data-id="element-200">
        {/* Fixed first columns with scraper IDs and names */}
        <div className="sticky left-0 z-10 flex" data-id="element-201">
          {/* ID Column */}
          <div data-id="element-202">
            <div className={`${getHeaderHeight()} flex items-center justify-center px-2 border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{
            width: '40px'
          }} data-id="element-203">
              <span className="font-extralight text-xs" data-id="element-204">ID</span>
            </div>
            {scrapers.map(scraper => {
            const {
              id
            } = extractNameAndId(scraper);
            return <div key={`id-${scraper.id}`} className={`${getRowHeight()} flex items-center justify-center px-2 border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700/50' : 'bg-white border-gray-200/50'}`} style={{
              width: '40px'
            }} data-id="element-205">
                  <span className="font-extralight text-xs" data-id="element-206">{id}</span>
                </div>;
          })}
          </div>
          {/* Name Column */}
          <div data-id="element-207">
            <div className={`${getHeaderHeight()} flex items-center justify-center px-2 border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-208">
              <span className="font-extralight text-xs" data-id="element-209">Scrapers</span>
            </div>
            {scrapers.map(scraper => {
            const {
              name
            } = extractNameAndId(scraper);
            return <div key={`name-${scraper.id}`} className={`${getRowHeight()} flex items-center px-2 border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700/50' : 'bg-white border-gray-200/50'}`} style={{
              minWidth: '160px',
              maxWidth: '160px'
            }} data-id="element-210">
                  <span className="font-extralight text-xs break-words line-clamp-2 w-full" data-id="element-211">
                    {name}
                  </span>
                </div>;
          })}
          </div>
        </div>
        {/* Scrollable columns for each day */}
        {visibleDays.map(day => {
        const formattedDate = formatDate(day.date);
        return <div key={day.date} style={{
          width: columnWidth
        }} data-id="element-212">
              <div className={`${getHeaderHeight()} flex flex-col items-center justify-center px-2 border-b border-l ${theme === 'dark' ? 'bg-gray-700/50 border-gray-700/50' : 'bg-gray-50 border-gray-200/50'}`} data-id="element-213">
                <span className="font-extralight text-xs" data-id="element-214">
                  {formattedDate.date}
                </span>
                <span className="font-light text-xs mt-1" data-id="element-215">
                  {formattedDate.day}
                </span>
              </div>
              {scrapers.map(scraper => {
            const amData = day.am.find(d => d.id === scraper.id);
            const pmData = day.pm.find(d => d.id === scraper.id);
            return <div key={`${scraper.id}-${day.date}`} className={`${getRowHeight()} flex items-center justify-center border-b border-l ${theme === 'dark' ? 'border-gray-700/50' : 'border-gray-200/50'}`} data-id="element-216">
                    <div className="grid grid-cols-2 gap-0 w-full h-full" data-id="element-217">
                      {/* AM Card */}
                      <StatusCard data={amData} time="AM" viewMode={viewMode} onCardClick={handleCardClick} isSelected={selectedCard !== null && selectedCard.data.id === amData?.id && selectedCard.data.date === amData?.date && selectedCard.time === 'AM'} isFiltered={colorFilter !== null && !isCardVisible(amData)} data-id="element-218" />
                      {/* PM Card */}
                      <StatusCard data={pmData} time="PM" viewMode={viewMode} onCardClick={handleCardClick} isSelected={selectedCard !== null && selectedCard.data.id === pmData?.id && selectedCard.data.date === pmData?.date && selectedCard.time === 'PM'} isFiltered={colorFilter !== null && !isCardVisible(pmData)} data-id="element-219" />
                    </div>
                  </div>;
          })}
            </div>;
      })}
      </div>
      {/* Fixed Modal for Selected Card */}
      {selectedCard && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal} data-id="element-220">
          <div className={`relative p-5 rounded-lg shadow-lg max-w-md w-full ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()} data-id="element-221">
            <button className={`absolute top-2 right-2 p-1 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} onClick={closeModal} data-id="element-222">
              <XIcon size={18} data-id="element-223" />
            </button>
            <div className="flex items-center mb-4" data-id="element-224">
              <div className="w-4 h-4 mr-2 rounded-sm" style={{
            backgroundColor: getCardBackgroundColor(selectedCard.data)
          }} data-id="element-225"></div>
              <h3 className="text-lg font-medium" data-id="element-226">
                {selectedCard.data.name} - {selectedCard.time}
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-227">
              <div data-id="element-228">
                <p className="text-sm font-light mb-1" data-id="element-229">Status</p>
                <p className="font-medium" data-id="element-230">
                  {getStatusFromDelta(selectedCard.data) === 'needsAttention' ? 'Needs Attention' : getStatusFromDelta(selectedCard.data).charAt(0).toUpperCase() + getStatusFromDelta(selectedCard.data).slice(1)}
                </p>
              </div>
              <div data-id="element-231">
                <p className="text-sm font-light mb-1" data-id="element-232">Date</p>
                <p className="font-medium" data-id="element-233">{selectedCard.data.date}</p>
              </div>
              <div data-id="element-234">
                <p className="text-sm font-light mb-1" data-id="element-235">URLs Scraped</p>
                <p className="font-medium" data-id="element-236">
                  {selectedCard.data.urlsScraped.toLocaleString()}
                </p>
              </div>
              <div data-id="element-237">
                <p className="text-sm font-light mb-1" data-id="element-238">Change</p>
                <p className={`font-medium ${selectedCard.data.delta > 0 ? 'text-green-500' : selectedCard.data.delta < 0 ? 'text-red-500' : ''}`} data-id="element-239">
                  {selectedCard.data.delta > 0 ? '+' : ''}
                  {selectedCard.data.delta.toLocaleString()} URLs
                </p>
              </div>
              <div data-id="element-240">
                <p className="text-sm font-light mb-1" data-id="element-241">Previous URLs</p>
                <p className="font-medium" data-id="element-242">
                  {selectedCard.data.previousUrls?.toLocaleString() || '0'}
                </p>
              </div>
              <div data-id="element-243">
                <p className="text-sm font-light mb-1" data-id="element-244">Percent Change</p>
                <p className={`font-medium ${selectedCard.data.delta > 0 ? 'text-green-500' : selectedCard.data.delta < 0 ? 'text-red-500' : ''}`} data-id="element-245">
                  {selectedCard.data.previousUrls ? `${(Math.abs(selectedCard.data.delta) / selectedCard.data.previousUrls * 100).toFixed(1)}%` : '0%'}
                </p>
              </div>
            </div>
            <div className="mt-4" data-id="element-246">
              <div className="w-full h-3 rounded-full" style={{
            backgroundColor: getCardBackgroundColor(selectedCard.data)
          }} data-id="element-247"></div>
            </div>
          </div>
        </div>}
    </div>;
};
interface SelectedCardData {
  data: ScraperData;
  time: 'AM' | 'PM';
}
export default ScraperStatusMap;